<script>
/* COMPOSANTS */
import AkButtonValidation from '@components/v2/general/AkButtonValidation.vue';
import AkDatatablePagination from '@components/v2/general/AkDatatablePagination.vue';
import GaListView from '@components/v2/layout/GaListView.vue';
import GaView from '@components/v2/layout/GaView';
import OtaTokenCreate from '@views/ota/OtaTokenCreate.vue';
import OtaTokenUpdate from '@views/ota/OtaTokenUpdate.vue';
import {Icon} from '@iconify/vue';

/* MIXINS */
import loaderMixin from '@mixins/loaderMixin';
import messageMixin from '@mixins/messageMixin';
import otaTokenPlatformConst from '@mixins/const/otaTokenPlatformConst';
import randomRef from '@mixins/randomRef';
import utilsMixin from '@mixins/utilsMixin';

/* SERVICES */
import otaTokenService from '@services/otaTokenService';
import {toPercent} from '@services/markupService';

export default {
  components: {
    AkButtonValidation,
    AkDatatablePagination,
    GaListView,
    GaView,
    Icon,
    OtaTokenCreate,
    OtaTokenUpdate,
  },
  mixins: [randomRef, loaderMixin, messageMixin, utilsMixin, otaTokenPlatformConst],
  head() {
    return {
      title: 'Comptes OTA',
    };
  },
  data() {
    return {
      list: [],
      filter: undefined,
      sidebarCreateVisible: false,
      sidebarUpdateVisible: false,
      item: {},
      loading: true,
      otaTokenId: undefined,
      displayDeleteDialog: false,
    };
  },
  computed: {
    listFilter() {
      if (!this.list) return [];
      if (this.filter) return this.list.filter((i) => i.platform === this.filter);
      return this.list;
    },
    sidebarVisible() {
      return this.sidebarUpdateVisible || this.sidebarCreateVisible;
    },
    sidebarTitle() {
      if (this.sidebarCreateVisible) return 'Ajouter un compte';
      if (this.sidebarUpdateVisible) return 'Modifier le libellé';
      return '';
    },
    currentPlatformLabel() {
      return this.filter ? this.displayForOtaTokenPlatformConst(this.filter) : 'Tous';
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    toPercent,
    hideSidebar() {
      this.sidebarCreateVisible = false;
      this.sidebarUpdateVisible = false;
    },
    refresh() {
      this.loading = true;
      otaTokenService.list().then((data) => {
        this.list = data;
        this.loading = false;
      });
    },
    selectFilter(platform) {
      this.filter = platform;
    },
    openDetails(event) {
      this.$router.push({name: 'settingsOtaTokenDetails', params: {id: event.data.id}});
    },
    toggleCreateSidebar() {
      this.sidebarCreateVisible = !this.sidebarCreateVisible;
      if (!this.sidebarCreateVisible) this.refresh();
    },
    toggleUpdateSidebar() {
      this.sidebarUpdateVisible = !this.sidebarUpdateVisible;
      if (!this.sidebarUpdateVisible) this.refresh();
    },
    openUpdateSidebar(item) {
      this.otaTokenId = item.id;
      this.sidebarUpdateVisible = true;
    },
    openDeleteDialog(item) {
      this.item = item;
      this.displayDeleteDialog = true;
    },
    closeDeleteDialog() {
      this.displayDeleteDialog = false;
    },
    deleteToken() {
      otaTokenService
        .delete(this.item)
        .then(() => {
          this.list = this.list.filter((val) => val.id !== this.item.id);
          this.success((this.item.label || this.item.otaAccountId) + ' a bien été supprimé');
          this.displayDeleteDialog = false;
        })
        .catch((e) => {
          this.addError(e.response?.data?.error ?? 'Une erreur est survenue');
          this.displayDeleteDialog = false;
        });
    },
  },
};
</script>

<template>
  <GaView title="Comptes OTA">
    <template #action>
      <div class="flex justify-end">
        <span @click="toggleCreateSidebar" class="btn-primary xs">Ajouter un compte</span>
      </div>
    </template>

    <template #content>
      <GaListView>
        <template #tabs>
          <div class="flex items-center">
            <span class="list-table-item" @click="selectFilter(undefined)" :class="[{active: filter === undefined}]">
              Tous
            </span>
            <span class="list-table-item" @click="selectFilter('AIRBNB')" :class="[{active: filter === 'AIRBNB'}]">
              Airbnb
            </span>
            <span class="list-table-item" @click="selectFilter('SMILY')" :class="[{active: filter === 'SMILY'}]">
              Smily
            </span>
          </div>
        </template>
        <template #content>
          <AkDatatablePagination :loading="loading" :value="listFilter" :lazy="false" @row-click="openDetails($event)">
            <template #empty>Aucun compte OTA configuré.</template>
            <Column header="Libellé" style="min-width: 200px">
              <template #body="slotProps">
                {{ slotProps.data.label }}
              </template>
            </Column>
            <Column header="ID du compte" style="min-width: 200px">
              <template #body="slotProps">
                {{ slotProps.data.otaAccountId }}
              </template>
            </Column>
            <Column v-if="!filter" header="Plateforme" style="width: 140px">
              <template #body="slotProps">
                {{ displayForOtaTokenPlatformConst(slotProps.data.platform) }}
              </template>
            </Column>
            <Column header="Mark-up général" style="width: 140px">
              <template #body="slotProps">
                {{
                  slotProps.data.generalMarkupPercent != null
                    ? toPercent(slotProps.data.generalMarkupPercent) + ' %'
                    : '-'
                }}
              </template>
            </Column>
            <Column body-style="text-align: right; overflow: visible" style="width: 120px">
              <template #body="slotProps">
                <div class="flex justify-end gap-2">
                  <span @click="openUpdateSidebar(slotProps.data)">
                    <i class="ga-icon ga-edit text-2xl text-grey-900 md:hover:cursor-pointer" />
                  </span>
                  <span @click="openDeleteDialog(slotProps.data)">
                    <i class="ga-icon ga-trash text-2xl text-red md:hover:cursor-pointer" />
                  </span>
                </div>
              </template>
            </Column>
          </AkDatatablePagination>
        </template>
      </GaListView>
    </template>

    <template #extra>
      <PDialog
        v-model:visible="displayDeleteDialog"
        header="Supprimer le compte"
        :modal="true"
        @hide="displayDeleteDialog = false">
        <div class="flex items-center gap-2">
          <Icon icon="fluent:warning-20-regular" class="text-red" width="26" />
          <span class="text-grey-900 font-normal">Confirmer la suppression de ce compte OTA ?</span>
        </div>
        <template #footer>
          <div class="flex w-full justify-center items-center gap-2">
            <span class="btn-grey xs min-w-20" @click="closeDeleteDialog">Non</span>
            <AkButtonValidation
              btn-class="btn-primary xs min-w-20"
              ref="btnDelete"
              label="Oui"
              @action="deleteToken()" />
          </div>
        </template>
      </PDialog>

      <Sidebar :visible="sidebarVisible" block-scroll :baseZIndex="9998" position="right">
        <template #container>
          <div class="p-sidebar-header">
            <div class="p-sidebar-header-content">{{ sidebarTitle }}</div>
            <button autofocus="" type="button" class="p-sidebar-close p-sidebar-icon p-link" @click="hideSidebar">
              <span
                class="p-sidebar-close-icon ga-icon ga-close_circle text-4xl text-grey-400 md:hover:text-grey-900 md:hover:bg-transparent"></span>
            </button>
          </div>
          <div class="p-sidebar-content">
            <perfect-scrollbar class="h-full flex flex-col gap-4 md:gap-2 pr-7" :options="{suppressScrollX: true}">
              <OtaTokenCreate v-if="sidebarCreateVisible" :platform="filter" @create-done="toggleCreateSidebar" />
              <OtaTokenUpdate :otaTokenId="otaTokenId" v-if="sidebarUpdateVisible" @create-done="toggleUpdateSidebar" />
            </perfect-scrollbar>
          </div>
        </template>
      </Sidebar>
    </template>
  </GaView>
</template>
